import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import extendOrder from "../api/app/extendOrder";
import getRoom from "../api/app/getRoom";
import getSimulatorReservations from "../api/app/getSimulatorReservations";
import PromotionMenu from "./PromotionMenu"; // Assuming you have this component
import ConfirmDialog from "../component/ConfirmDialog";
import { useNavigate } from "react-router-dom"; // Assuming you use react-router for navigation

// Parse ISO string as local time (no UTC conversion)
const parseRawDateTime = (dateTimeStr) => {
  const match = dateTimeStr.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):?(\d{2})?/);
  if (match) {
    const [, year, month, day, hour, minute, second = "0"] = match;
    return new Date(
      parseInt(year),
      parseInt(month) - 1,
      parseInt(day),
      parseInt(hour),
      parseInt(minute),
      parseInt(second)
    );
  }
  return new Date(dateTimeStr);
};

const formatDate = (date) => {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const dd = `${d.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

const ExtendDialog = ({ open, onClose, order }) => {
  const [table, setTable] = useState({});
  const [selectedPriceTableIndex, setSelectedPriceTableIndex] = useState(-1);
  const [selectedStartTime, setSelectedStartTime] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [dialogContent, setDialogContent] = useState("");
  const bufferTime = 10;
  const navigate = useNavigate(); // To handle navigation

  // 時間衝突檢測狀態
  const [reservedTimes, setReservedTimes] = useState([]);
  const [timeOverlap, setTimeOverlap] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState("");

  useEffect(() => {
    const fetchRooms = async () => {
      try {
        setLoading(true);
        console.log("[ExtendDialog] Fetching rooms for storeId:", order?.StoreCode);
        const resp = await getRoom(order?.StoreCode);
        console.log("[ExtendDialog] getRoom response:", resp);

        if (!resp) {
          setTable({});
          return;
        }

        // Find the simulator matching Table code (order.TableCode or order.VenueId)
        const simulators = (resp.simulators ?? [])
          .filter((sim) => sim.isActive !== false)
          .sort((a, b) => String(a.code || "").localeCompare(String(b.code || "")));

        console.log("[ExtendDialog] Order data:", {
          StoreCode: order?.StoreCode,
          TableCode: order?.TableCode,
          VenueId: order?.VenueId,
          storeSimulatorId: order?.storeSimulatorId,
        });
        console.log("[ExtendDialog] Available simulators:", simulators.map(s => ({ id: s.id, code: s.code, name: s.name })));

        const foundSim = simulators.find(
          (sim) => sim.code === order?.TableCode || sim.id === order?.VenueId || sim.id === order?.storeSimulatorId
        );
        console.log("[ExtendDialog] Found simulator:", foundSim ? { id: foundSim.id, code: foundSim.code, name: foundSim.name } : null);

        if (foundSim) {
          // Build PriceTable from plans (like RoomList)
          const priceTable = {
            PlanId: [],
            Hour: [],
            Price: [],
            FreeHour: [],
            Discount: [],
          };
          (foundSim.plans ?? []).forEach((p) => {
            priceTable.PlanId.push(p.id);
            priceTable.Hour.push(parseFloat(p.hours));
            priceTable.Price.push(p.price);
            priceTable.FreeHour.push(0);
            priceTable.Discount.push(0);
          });

          // Fetch reservations (API returns BOOKED periods)
          // We need to convert them to AVAILABLE periods (gaps between bookings)
          // because isTimeReserved checks if time overlaps with Reservations
          let reservations = [];
          try {
            const reservationData = await getSimulatorReservations(
              foundSim.id,
              formatDate(new Date())
            );
            console.log("[ExtendDialog] Booked periods from API:", reservationData);

            if (reservationData && Array.isArray(reservationData)) {
              // 儲存原始已預約時段供衝突檢測使用
              setReservedTimes(reservationData);

              // Convert booked periods to available periods (matching RoomList logic)
              let currentTime = new Date(); // Start from now

              reservationData.forEach((period) => {
                const periodStart = parseRawDateTime(period.startTime);
                const periodEnd = parseRawDateTime(period.endTime);

                // If there's a gap before this booking, that's an available period
                if (currentTime < periodStart) {
                  reservations.push({
                    StartTime: currentTime,
                    EndTime: periodStart,
                  });
                }
                currentTime = periodEnd;
              });

              // Add remaining period until end of day
              const endOfDay = new Date(new Date().setHours(23, 59, 59, 999));
              if (currentTime < endOfDay) {
                reservations.push({
                  StartTime: currentTime,
                  EndTime: endOfDay,
                });
              }

              console.log("[ExtendDialog] Converted to available periods:", reservations);
            }
          } catch (err) {
            console.error("[ExtendDialog] Error fetching reservations:", err);
            setReservedTimes([]);
          }

          setTable({
            ...foundSim,
            Code: foundSim.code,
            Name: foundSim.name,
            PriceTable: priceTable,
            Reservations: reservations,
          });
        } else {
          setTable({});
        }
      } catch (err) {
        console.error("[ExtendDialog] Error:", err);
        setTable({});
      } finally {
        setLoading(false);
      }
    };

    if (open && order) {
      fetchRooms();
      setSelectedStartTime(new Date(order.ReservationEndTime));
    }
  }, [open, order]);

  const handleSelectPriceTableIndex = (index) => {
    setSelectedPriceTableIndex(index);

    // 立即計算結束時間並檢查重疊
    if (index !== -1 && table?.PriceTable && reservedTimes.length > 0) {
      const totalHours =
        table.PriceTable.Hour[index] + (table.PriceTable.FreeHour[index] || 0);
      const startTime = new Date(order.ReservationEndTime);
      const endTime = new Date(order.ReservationEndTime);
      const hours = Math.floor(totalHours);
      const minutes = (totalHours - hours) * 60;
      endTime.setHours(endTime.getHours() + hours, endTime.getMinutes() + minutes);

      const startMs = startTime.getTime();
      const endMs = endTime.getTime();

      const hasOverlap = reservedTimes.some((reservation) => {
        const reservedStart = parseRawDateTime(reservation.startTime).getTime();
        const reservedEnd = parseRawDateTime(reservation.endTime).getTime();
        return startMs < reservedEnd && reservedStart < endMs;
      });

      setTimeOverlap(hasOverlap);
      if (hasOverlap) {
        setToastMsg("此時段已有預約，請選擇其他時間");
        setToastOpen(true);
      }
    } else {
      setTimeOverlap(false);
    }
  };

  const isTimeReserved = (ptime, type) => {
    const time = new Date(ptime.setMilliseconds(0)).setSeconds(0);

    if (!table?.Reservations || table.Reservations.length === 0) {
      return false;
    }
    let startTime = new Date(order.ReservationEndTime);
    startTime.setMinutes(startTime.getMinutes() + bufferTime);
    if (type === "hour") startTime = time;
    if (type === "minute") startTime = time;
    for (let reservation of table.Reservations) {
      const rStartTime = new Date(reservation.StartTime);
      const rEndTime = new Date(reservation.EndTime);
      rStartTime.setMinutes(rStartTime.getMinutes() - bufferTime);
      rEndTime.setMinutes(rEndTime.getMinutes() + bufferTime);

      if (
        (startTime < rEndTime && startTime > rStartTime) ||
        startTime === rStartTime ||
        time === rEndTime
      )
        return true;
    }

    return false;
  };

  const handlePaymentClicked = (orderId, index) => {
    const newOrder = async () => {
      try {
        // Calculate start and end times based on selection
        const priceItem = table.PriceTable;
        const hours = priceItem?.Hour?.[index] || 0;
        const freeHours = priceItem?.FreeHour?.[index] || 0;
        const totalHours = hours + freeHours;

        const startTime = new Date(order.ReservationEndTime).toISOString();
        const endDate = new Date(order.ReservationEndTime);
        endDate.setHours(endDate.getHours() + Math.floor(totalHours));
        endDate.setMinutes(endDate.getMinutes() + (totalHours - Math.floor(totalHours)) * 60);
        const endTime = endDate.toISOString();

        const planId = priceItem?.PlanId?.[index] || priceItem?.Id?.[index] || "";
        const storeSimulatorId = order.VenueId || order.storeSimulatorId || "";

        console.log("[ExtendDialog] Calling extendOrder API:", { orderId, startTime, endTime, planId, storeSimulatorId });
        const responseData = await extendOrder(
          orderId,
          startTime,
          endTime,
          planId,
          storeSimulatorId
        );
        console.log("[ExtendDialog] extendOrder response:", responseData);

        // Handle payment form response (like ReservationForm.jsx)
        if (responseData && responseData.payload) {
          const { properties, data } = responseData.payload;

          // Create hidden form fields from response data
          const fields = Object.entries(data)
            .map(
              ([k, v]) =>
                `${k} <input type="text" name="${k}" readonly value="${v}" /><br/>`
            )
            .join("\n");

          const htmlContent = `
            <html>
              <body>
                <form id="myForm" action="${properties.url}" method="${properties.method}">
                  ${fields}
                </form>
                <script>document.getElementById('myForm').submit();</script>
              </body>
            </html>
          `;
          const blob = new Blob([htmlContent], { type: "text/html" });
          const url = URL.createObjectURL(blob);
          window.location.href = url;
          return;
        }

        // Fallback for old response format
        if (responseData.code === "000" || responseData.code === "0001") {
          const newOrderId = responseData.order_id || responseData.data?.id;
          if (responseData.url === "" && responseData.code === "0001") {
            navigate(`/app/order/orderResult/${newOrderId}`);
          } else if (responseData.url) {
            window.location.href = responseData.url;
          } else {
            navigate(`/app/order/orderResult/${newOrderId}`);
          }
        } else {
          setDialogContent("網路錯誤或是座位被訂走，請重新再試。\n(1)");
          setConfirmDialog(true);
        }
      } catch (err) {
        console.error("[ExtendDialog] Error:", err);
        setDialogContent("網路錯誤或是座位被訂走，請重新再試。\n(2)");
        setConfirmDialog(true);
      }
    };
    newOrder();
  };

  const handleConfirmDialogClicked = () => {
    setConfirmDialog(false);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"延長時間"}</DialogTitle>
        <DialogContent>
          {!loading && (
            <>
              <PromotionMenu
                priceTable={table.PriceTable}
                selectedStartTime={selectedStartTime}
                handleSelectPriceTableIndex={handleSelectPriceTableIndex}
                isTimeReserved={isTimeReserved}
              />
              {timeOverlap && (
                <Alert
                  severity="warning"
                  icon={<WarningAmberIcon />}
                  sx={{ mt: 2, borderRadius: 2, fontWeight: 600 }}
                >
                  此時段已有預約，請選擇其他時間
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <>
            <Button onClick={onClose} color="info">
              取消
            </Button>
            <Button
              onClick={() => {
                handlePaymentClicked(order.OrderId, selectedPriceTableIndex);
                onClose();
              }}
              color="info"
              autoFocus
              disabled={timeOverlap || selectedPriceTableIndex === -1}
            >
              結帳
            </Button>
          </>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={confirmDialog}
        content={dialogContent}
        confirmOnly={true}
        onClose={handleConfirmDialogClicked}
      />
      <Snackbar
        open={toastOpen}
        autoHideDuration={2500}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToastOpen(false)}
          severity="warning"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toastMsg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ExtendDialog;
